import { endOfMonth, format, parseISO, startOfMonth } from "date-fns";
import { AccountingReportCycle } from "@common/constants";
import { ScheduleApplicationStatus } from "@common/types/domains/accounting/schedule";
import { SchedulesReportLayout } from "@common/types/reports/schedules-report";
import {
    PERIOD_ROLLFORWARD_VIEW_ID,
    WATERFALL_BY_MONTH_VIEW_ID,
} from "@components/accounting/schedulesReport/BuiltInSchedulesReportViews";
import { buildSchedulesReportUrl } from "@components/accounting/schedulesReport/schedulesReportDeepLink";
import { ScheduleApplicationParentRow } from "./scheduleApplicationRowHelpers";
import { getUniqueScheduleApplicationIds } from "./scheduleApplicationRowId";

/** The default delimiter of use-query-params' delimited array params. */
const QUERY_ARRAY_DELIMITER = "_";

function toDateOnly(date: Date): string {
    return format(date, "yyyy-MM-dd");
}

function resolveReportRange(options: {
    selectedRows: ScheduleApplicationParentRow[];
    periodStart: string | null;
    periodEnd: string | null;
}): { startDate: string; endDate: string } {
    const dates = options.selectedRows
        .flatMap((row) => [
            row.date,
            ...row.children.map((child) => child.date),
        ])
        .sort();
    return {
        startDate:
            options.periodStart ?? toDateOnly(startOfMonth(parseISO(dates[0]))),
        endDate:
            options.periodEnd ??
            toDateOnly(endOfMonth(parseISO(dates[dates.length - 1]))),
    };
}

export function getReportableScheduleRows(
    rows: ScheduleApplicationParentRow[],
): ScheduleApplicationParentRow[] {
    return rows.filter((row) => row.status !== ScheduleApplicationStatus.draft);
}

/** Opens the Schedules report scoped to exactly these schedules, by month. */
export function buildSelectedSchedulesReportUrl(options: {
    selectedRows: ScheduleApplicationParentRow[];
    layout: SchedulesReportLayout;
    periodStart: string | null;
    periodEnd: string | null;
}): string | null {
    const selectedRows = getReportableScheduleRows(options.selectedRows);
    const [anchor] = selectedRows;
    if (!anchor) {
        return null;
    }
    const scheduleIds = getUniqueScheduleApplicationIds(selectedRows);
    const entityIds = [...new Set(selectedRows.map((row) => row.entityId))];
    const { startDate, endDate } = resolveReportRange({
        ...options,
        selectedRows,
    });
    const viewId =
        options.layout === SchedulesReportLayout.waterfall
            ? WATERFALL_BY_MONTH_VIEW_ID
            : PERIOD_ROLLFORWARD_VIEW_ID;
    const search = new URLSearchParams({
        entityId: String(anchor.entityId),
        ledgerId: anchor.ledgerId,
        startDate,
        endDate,
        cycle: AccountingReportCycle.MONTH,
        layout: options.layout,
        scheduleIds: scheduleIds.join(QUERY_ARRAY_DELIMITER),
    });
    if (entityIds.length > 1) {
        search.set("entityIds", entityIds.join(QUERY_ARRAY_DELIMITER));
    }
    return buildSchedulesReportUrl(search.toString(), viewId);
}
